import * as domain from "../domain/index"
import * as api from "../clients/slashid-api"
import * as time from "../utils/time"
import { SlashID } from "../main"
import { Configuration, DefaultConfig } from "../clients/slashid-api"
import { OTPMethodOptions } from "./options"
import { versionInfo as version } from "../version"
import * as errors from "./errors"
import * as errors_INTERNAL_ONLY from "./errors.internal"
import * as events from "../utils/events"
import * as remote from "../utils/remote"
import * as base64 from "../utils/base64"
import { UserTokenText } from "../domain/index"
import { createValidationRules, validate } from "./passwords"
import { EventEmitter, EventHandler } from "./emitter"
import { createRetryOnNetworkErrorMiddleware } from "../utils/fetch"
import { Attestation, Challenge } from "./challenges.types"

export type FlowMeta = {
    authFlowId?: string | undefined
    recoveryFlowId?: string | undefined
}

const OTP_CHALLENGES = new Set<api.ChallengeType>([
    api.ChallengeType.Otp,
    api.ChallengeType.TotpRegister,
    api.ChallengeType.TotpVerify,
])

export function isOTPChallengeType(challenge: api.ChallengeType) {
    return OTP_CHALLENGES.has(challenge)
}

export class ChallengeProcessor {
    private authnClient: api.DefaultApi
    private slashID: SlashID
    private readonly emitter: EventEmitter<events.NamedEvents>
    private flowMeta: FlowMeta | undefined
    private flowCancelled = false

    constructor(slashID: SlashID, emitter: EventEmitter<events.NamedEvents>, flowMeta?: FlowMeta | undefined) {
        // TODO do we need the whole config?
        this.slashID = slashID
        this.authnClient = new api.DefaultApi(
            new Configuration({
                ...DefaultConfig,
                basePath: this.slashID.baseURL,
                middleware: [
                    createRetryOnNetworkErrorMiddleware({
                        maxRetries: 5,
                        baseDelayMs: 500,
                    }),
                ],
            })
        )
        this.emitter = emitter
        this.flowMeta = flowMeta

        this.listenForEvents()
    }

    private listenForEvents() {
        this.emitter.on("flowCancelled", () => {
            this.flowCancelled = true
        })
    }

    private updateClient(token?: UserTokenText) {
        this.authnClient = new api.DefaultApi(
            new Configuration({
                ...DefaultConfig,
                basePath: this.slashID.baseURL,
                middleware: [
                    createRetryOnNetworkErrorMiddleware({
                        maxRetries: 5,
                        baseDelayMs: 500,
                    }),
                ],
                ...(token && { accessToken: token }),
            })
        )
    }

    private notifySlashID = (key: keyof events.NamesToEventsMap, event: events.NamedEvents["payload"]) => {
        // @ts-expect-error payload types do not match because of string & void types
        this.emitter.emit({
            name: key,
            payload: event,
            meta: { access: "public", ...this.flowMeta },
        })
    }

    public async processChallenges({
        challenges,
        authnFactor,
        previousToken,
    }: {
        challenges: Challenge[]
        authnFactor?: domain.Factor
        previousToken?: UserTokenText
    }): Promise<domain.UserTokenText> {
        this.updateClient(previousToken)

        let userToken: domain.UserTokenText | undefined = undefined

        // TODO make sure the challenge detail is added to the error if it happens
        for (const ch of challenges) {
            userToken = await this.process(ch, authnFactor)
        }

        if (!userToken) {
            throw new Error("no user token in attestation response")
        }

        return userToken
    }

    private async process(challenge: Challenge, authnFactor?: domain.Factor): Promise<domain.UserTokenText> {
        let attestation: Attestation

        switch (challenge.type) {
            case api.ChallengeType.Proxy:
                return this.processProxyChallenge(challenge as api.ProxyChallenge)
            case api.ChallengeType.Nonce:
                attestation = await this.processNonceChallenge(challenge as api.NonceChallenge)
                break
            case api.ChallengeType.Otp: {
                const otpOpts = new OTPMethodOptions(authnFactor?.options)
                attestation = await this.processOTPChallenge(challenge as api.OTPChallenge, otpOpts)
                break
            }
            case api.ChallengeType.TotpRegister: {
                attestation = await this.processTOTPRegisterChallenge(challenge as api.TOTPRegisterChallenge)
                break
            }
            case api.ChallengeType.TotpVerify: {
                attestation = await this.processTOTPVerifyChallenge(challenge as api.TOTPVerifyChallenge)
                break
            }
            case api.ChallengeType.WebauthnGet:
                attestation = await this.processWebAuthnGetChallenge(challenge as api.WebAuthnGetChallenge)
                break
            case api.ChallengeType.WebauthnCreate:
                attestation = await this.processWebAuthnCreateChallenge(challenge as api.WebAuthnCreateChallenge)
                break
            case api.ChallengeType.Oidc:
                return this.processOIDCChallenge(
                    challenge as api.OIDCChallenge,
                    authnFactor as unknown as domain.OIDCWithExtras
                )
            case api.ChallengeType.Saml:
                return this.processSAMLChallenge(
                    challenge as api.SAMLChallenge,
                    authnFactor as unknown as domain.SAMLWithExtras
                )
            case api.ChallengeType.PasswordSet:
                attestation = await this.processPasswordSetChallenge(challenge as api.PasswordSetChallenge)
                break
            case api.ChallengeType.PasswordVerify:
                attestation = await this.processPasswordVerifyChallenge(challenge as api.PasswordVerifyChallenge)
                break
            case api.ChallengeType.PasswordReset:
                attestation = await this.processPasswordResetChallenge(challenge as api.PasswordResetChallenge)
                break
            case api.ChallengeType.UiCustomization:
                return this.processUiCustomizationChallenge(challenge as api.UICustomizationChallenge)
            default:
                throw new Error("unhandled challenge type: " + JSON.stringify(challenge))
        }

        let response
        let headers: Headers
        let originalError: any
        try {
            const resp = await this.authnClient.postAttestationV3Raw(
                {
                    // @ts-expect-error challenge_type is typed as possible undefined
                    attestation: attestation,
                    slashIDSdkVersion: version.raw,
                },
                { cache: "no-cache" }
            )
            response = await resp.value()
            headers = resp.raw.headers
        } catch (e: any) {
            if (errors_INTERNAL_ONLY.isResponseError(e)) {
                response = await e.response.clone().json()
                headers = e.response.headers
                originalError = e
            } else {
                throw e
            }
        }

        const headersIncludesLocation = headers.has("location")

        if (headersIncludesLocation) {
            const redirectUri = headers.get("location")
            if (typeof redirectUri === "string" && Boolean(redirectUri)) {
                this.emitter.emit({
                    name: "redirectUriDiscovered",
                    payload: {
                        redirectUri,
                    },
                    meta: { access: "public" },
                })
            }
        }

        if (errors_INTERNAL_ONLY.isThrownResponse(response) && response.errors.length) {
            // retryable errors
            if (response.errors[0].httpcode === 400 && challenge.type === api.ChallengeType.PasswordSet) {
                // password validation failed server side
                this.notifySlashID("invalidPasswordSubmitted", {
                    failedRules: [],
                    authenticationFactor: authnFactor,
                    challengeId: challenge.id,
                    failureDetail: response.errors[0].message,
                })
                return this.process(challenge, authnFactor)
            }

            if (response.errors[0].httpcode === 401 && challenge.type === api.ChallengeType.PasswordVerify) {
                // password does not match what is recorded for the given handle
                this.notifySlashID("incorrectPasswordSubmitted", {
                    authenticationFactor: authnFactor,
                    challengeId: challenge.id,
                    failureDetail: response.errors[0].message,
                })
                return this.process(challenge, authnFactor)
            }

            if (response.errors[0].httpcode === 401 && isOTPChallengeType(challenge.type)) {
                // unlike all other challenges OTPs are potentially subject to user errors => retry on Unauthorized
                this.notifySlashID("otpIncorrectCodeSubmitted", { factor: authnFactor!, challengeId: challenge.id })
                return this.process(challenge, authnFactor)
            }

            const sidError = await errors_INTERNAL_ONLY.toSlashIDError(originalError)
            throw sidError
        }

        const userToken = response.result.token
        if (!userToken) {
            throw new Error("no user token in attestation response")
        }

        if (challenge.type === api.ChallengeType.WebauthnCreate) {
            const options = challenge.options as any
            this.slashID.credentialStorage.add(options.rp.id, (attestation.data as any).rawId)
        }

        return userToken
    }

    private async processProxyChallenge(challenge: api.ProxyChallenge): Promise<UserTokenText> {
        let userToken: UserTokenText | undefined = undefined

        while (!userToken) {
            if (this.flowCancelled) {
                throw new errors_INTERNAL_ONLY.SlashIDError({
                    name: errors.ERROR_NAMES.flowCancelled,
                    message: "The flow has been cancelled!",
                    context: {
                        challenge,
                    },
                })
            }

            await time.sleep(1000)

            // TODO review
            const response = await this.authnClient.getChallengeChallengeIdV2(
                {
                    challengeId: challenge.options!!.challenge_id!!,
                    slashIDSdkVersion: version.raw,
                },
                { cache: "no-cache" }
            )
            if (response.errors) {
                if (response.errors.length !== 1 || response.errors[0].httpcode !== 202) {
                    throw response
                }
            }

            userToken = response.result
        }

        return userToken
    }

    private async processNonceChallenge(challenge: api.NonceChallenge): Promise<api.NonceAttestation> {
        const attestation: api.NonceAttestation = {
            challenge_id: challenge.id,
            challenge_type: challenge.type,
            data: {
                challenge: challenge.options?.challenge,
            },
        }

        return attestation
    }

    private shouldProcessEvent(event: events.NamedEvents): boolean {
        if (!event.meta) {
            return true
        }

        if (!event.meta.authFlowId && !event.meta.recoveryFlowId) {
            return true
        }

        if (this.flowMeta?.authFlowId && event.meta?.authFlowId !== this.flowMeta.authFlowId) {
            return false
        }
        if (this.flowMeta?.recoveryFlowId && event.meta?.recoveryFlowId !== this.flowMeta.recoveryFlowId) {
            return false
        }
        return true
    }

    private async processOTPChallenge(
        challenge: api.OTPChallenge,
        otpFactorOptions: OTPMethodOptions
    ): Promise<api.OTPAttestation> {
        this.notifySlashID("otpSmsSent")
        this.notifySlashID("otpCodeSent")
        const handleOtpInput = new Promise<string>((resolve) => {
            const getOtp: EventHandler<events.EventsWithNamesMap["otpCodeSubmitted"]> = (
                event: events.EventsWithNamesMap["otpCodeSubmitted"]
            ) => {
                if (!this.shouldProcessEvent(event)) {
                    return
                }

                // @ts-expect-error payload types do not match because of string & void types
                this.emitter.off("otpCodeSubmitted", getOtp)
                resolve(event.payload as string)
            }

            // @ts-expect-error payload types do not match because of string & void types
            this.emitter.on("otpCodeSubmitted", getOtp)
        })

        // backward compatibility - in case a client sends in getOTP we wrap it with the event
        if (typeof otpFactorOptions.getOTP === "function") {
            otpFactorOptions.getOTP().then((otp) => this.notifySlashID("otpCodeSubmitted", otp!!))
        }

        const otpCode = await handleOtpInput

        if (!otpCode) {
            throw new Error("no OTP code provided")
        }

        const attestation: api.OTPAttestation = {
            challenge_id: challenge.id,
            challenge_type: challenge.type,
            data: {
                otp: otpCode,
            },
        }

        return attestation
    }

    private async processTOTPRegisterChallenge(
        challenge: api.TOTPRegisterChallenge
    ): Promise<api.TOTPRegisterAttestation> {
        this.notifySlashID("totpKeyGenerated", {
            uri: challenge.options?.key_uri!,
            qrCode: challenge.options?.qr_code_data_b64!,
            recoveryCodes: challenge.options?.recovery_codes!,
        })

        const handleTotpInput = new Promise<string>((resolve) => {
            const getOtp: EventHandler<events.EventsWithNamesMap["otpCodeSubmitted"]> = (
                event: events.EventsWithNamesMap["otpCodeSubmitted"]
            ) => {
                if (!this.shouldProcessEvent(event)) {
                    return
                }

                // @ts-expect-error payload types do not match because of string & void types
                this.emitter.off("otpCodeSubmitted", getOtp)
                resolve(event.payload as string)
            }

            // @ts-expect-error payload types do not match because of string & void types
            this.emitter.on("otpCodeSubmitted", getOtp)
        })

        const totpCode = await handleTotpInput
        if (!totpCode) {
            throw new errors_INTERNAL_ONLY.SlashIDError({
                name: errors.ERROR_NAMES.invalidActionError,
                message: "no TOTP code provided",
                context: {
                    challengeId: challenge.id,
                },
            })
        }

        const attestation: api.TOTPRegisterAttestation = {
            challenge_id: challenge.id,
            challenge_type: challenge.type,
            data: {
                otp: totpCode,
            },
        }
        return attestation
    }

    public async processTOTPVerifyChallenge(challenge: api.TOTPVerifyChallenge): Promise<api.TOTPVerifyAttestation> {
        this.notifySlashID("totpCodeRequested")

        const handleTotpInput = new Promise<string>((resolve) => {
            const getOtp: EventHandler<events.EventsWithNamesMap["otpCodeSubmitted"]> = (
                event: events.EventsWithNamesMap["otpCodeSubmitted"]
            ) => {
                if (!this.shouldProcessEvent(event)) {
                    return
                }

                // @ts-expect-error payload types do not match because of string & void types
                this.emitter.off("otpCodeSubmitted", getOtp)
                resolve(event.payload as string)
            }

            // @ts-expect-error payload types do not match because of string & void types
            this.emitter.on("otpCodeSubmitted", getOtp)
        })

        const totpCode = await handleTotpInput
        if (!totpCode) {
            throw new errors_INTERNAL_ONLY.SlashIDError({
                name: errors.ERROR_NAMES.invalidActionError,
                message: "no TOTP code provided",
                context: {
                    challengeId: challenge.id,
                },
            })
        }

        const attestation: api.TOTPVerifyAttestation = {
            challenge_id: challenge.id,
            challenge_type: challenge.type,
            data: {
                otp: totpCode,
            },
        }
        return attestation
    }

    public async processWebAuthnGetChallenge(challenge: api.WebAuthnGetChallenge): Promise<api.WebAuthnGetAttestation> {
        // Calling WebAuthn API needs focus
        const hadFocus = await events.waitForFocus()

        const options: any = challenge.options

        if (!this.slashID.isLocalScope(options.rpId)) {
            const sdkURL = this.slashID.getSDKUrl()
            let sdkWindow: Window | null

            if (await SlashID.canGetPublicKeyCredentialsInIFrame()) {
                sdkWindow = this.slashID.iframe!!.contentWindow
                if (!sdkWindow) {
                    throw new Error("cannot get global id iframe")
                }
            } else {
                // Empirically if we don't wait here the "popup"
                // on Chrome is created but doesn't get focus
                if (!hadFocus) {
                    await time.sleep(200)
                }

                // no target, features args => should open a tab, not a popup
                sdkWindow = window.open(sdkURL.toString())
                if (!sdkWindow) {
                    throw new Error("cannot open global id window")
                }
            }

            const attestationPromise = new Promise<api.WebAuthnGetAttestation>((resolve, reject) => {
                const request = new domain.WebAuthnGetRequestMessage(
                    { baseURL: this.slashID.baseURL, sdkURL: this.slashID.sdkURL },
                    challenge
                )

                const responseListener = (event: MessageEvent) => {
                    const response: domain.WebAuthnGetResponseMessage = event.data
                    if (response.id !== request.id) {
                        return
                    }
                    // has no effect on an IFrame
                    sdkWindow!!.close()

                    window.removeEventListener("message", responseListener)
                    if (response.error) {
                        reject(response.error)
                    } else {
                        resolve(response.attestation!!)
                    }
                }
                window.addEventListener("message", responseListener)

                // TODO handle retries
                remote.ping(sdkWindow!!).then((win) => {
                    win.postMessage(request, sdkURL.origin)
                })
            })
            return attestationPromise
        } else {
            const decodedOptions: PublicKeyCredentialRequestOptions = {
                challenge: base64.decode(options.challenge),
                extensions: options.extensions,
                rpId: options.rpId,
                timeout: options.timeout,
                userVerification: options.userVerification,
            }

            const allowCredentials: any[] | null = options.allowCredentials
            if (allowCredentials) {
                decodedOptions.allowCredentials = new Array<PublicKeyCredentialDescriptor>(allowCredentials.length)
                for (let idx = 0; idx < allowCredentials.length; idx++) {
                    const credential = allowCredentials[idx]
                    decodedOptions.allowCredentials[idx] = {
                        id: base64.decode(credential.id),
                        type: credential.type,
                        transports: credential.transports,
                    }
                }
            }

            const credential = (await navigator.credentials.get({
                publicKey: decodedOptions,
            })) as PublicKeyCredential | null
            if (!credential) {
                throw new Error("could not create assert credential")
            }

            const attestationResponse = credential.response as AuthenticatorAssertionResponse
            const rawId = base64.encode(new Uint8Array(credential.rawId))
            const encodedCredential = {
                id: credential.id,
                rawId,
                type: credential.type,
                clientExtensionResults: credential.getClientExtensionResults(),
                response: {
                    clientDataJSON: base64.encode(new Uint8Array(attestationResponse.clientDataJSON)),
                    authenticatorData: base64.encode(new Uint8Array(attestationResponse.authenticatorData)),
                    signature: base64.encode(new Uint8Array(attestationResponse.signature)),
                    userHandle: attestationResponse.userHandle
                        ? base64.encode(new Uint8Array(attestationResponse.userHandle))
                        : null,
                },
            }

            const attestation: api.WebAuthnGetAttestation = {
                challenge_id: challenge.id,
                challenge_type: challenge.type,
                data: encodedCredential,
            }

            this.notifySlashID("webAuthnChallengeProcessed", { credentialId: rawId })

            return attestation
        }
    }

    public async processWebAuthnCreateChallenge(
        challenge: api.WebAuthnCreateChallenge
    ): Promise<api.WebAuthnCreateAttestation> {
        // Calling WebAuthn API needs focus
        const hadFocus = await events.waitForFocus()

        const options: any = challenge.options

        if (!this.slashID.isLocalScope(options.rp.id)) {
            // Empirically if we don't wait here the "popup"
            // on Chrome is created but doesn't get focus
            if (!hadFocus) {
                await time.sleep(200)
            }

            const attestationPromise = new Promise<api.WebAuthnCreateAttestation>((resolve, reject) => {
                const sdkURL = this.slashID.getSDKUrl()

                // no target, features args => should open a tab, not a popup
                const sdkWindow = window.open(sdkURL.toString())
                if (!sdkWindow) {
                    reject(new Error("cannot open global id window"))
                    return
                }

                const request = new domain.WebAuthnCreateRequestMessage(
                    { baseURL: this.slashID.baseURL, sdkURL: this.slashID.sdkURL },
                    challenge
                )

                const responseListener = (event: MessageEvent) => {
                    const response: domain.WebAuthnCreateResponseMessage = event.data
                    if (response.id !== request.id) {
                        return
                    }
                    sdkWindow.close()

                    window.removeEventListener("message", responseListener)
                    if (response.error) {
                        reject(response.error)
                    } else {
                        resolve(response.attestation!!)
                    }
                }
                window.addEventListener("message", responseListener)

                // TODO handle retries
                remote.ping(sdkWindow).then((win) => {
                    win.postMessage(request, sdkURL.origin)
                })
            })
            return attestationPromise
        } else {
            const decodedOptions: PublicKeyCredentialCreationOptions = {
                challenge: base64.decode(options.challenge),
                rp: options.rp,
                user: {
                    id: base64.decode(options.user.id),
                    name: options.user.name,
                    displayName: options.user.displayName,
                },
                pubKeyCredParams: options.pubKeyCredParams,
                authenticatorSelection: options.authenticatorSelection,
                timeout: options.timeout,
                extensions: options.extensions,
                attestation: options.attestation,
            }

            const excludeCredentials: any[] | null = options.excludeCredentials
            if (excludeCredentials) {
                decodedOptions.excludeCredentials = new Array<PublicKeyCredentialDescriptor>(excludeCredentials.length)
                for (let idx = 0; idx < excludeCredentials.length; idx++) {
                    const credential = excludeCredentials[idx]
                    decodedOptions.excludeCredentials[idx] = {
                        id: base64.decode(credential.id),
                        type: credential.type,
                        transports: credential.transports,
                    }
                }
            }

            const credential = (await navigator.credentials.create({
                publicKey: decodedOptions,
            })) as PublicKeyCredential | null
            if (!credential) {
                throw new Error("cannot create credential")
            }

            const attestationResponse = credential.response as AuthenticatorAttestationResponse
            const rawId = base64.encode(new Uint8Array(credential.rawId))
            const encodedCredential = {
                id: credential.id,
                rawId,
                type: credential.type,
                clientExtensionResults: credential.getClientExtensionResults(),
                response: {
                    attestationObject: base64.encode(new Uint8Array(attestationResponse.attestationObject)),
                    clientDataJSON: base64.encode(new Uint8Array(attestationResponse.clientDataJSON)),
                },
            }

            const attestation: api.WebAuthnCreateAttestation = {
                challenge_id: challenge.id,
                challenge_type: challenge.type,
                data: encodedCredential,
            }

            this.notifySlashID("webAuthnChallengeProcessed", { credentialId: rawId })

            return attestation
        }
    }

    private async processOIDCChallenge(
        challenge: api.OIDCChallenge,
        authenticationFactor: domain.OIDCWithExtras
    ): Promise<string> {
        if (authenticationFactor.options.ux_mode === api.OIDCUXMode.Popup) {
            const handle = authenticationFactor.extras.setPopupUrl!!(challenge.options.auth_code_url)

            if (!challenge?.options?.auth_code_url) {
                this.notifySlashID("oauthFlowFailed", {
                    ...authenticationFactor.options,
                    reason: events.OAuthFailureReason.BadOAuthURL,
                })
                throw new Error(`Bad OAuth URL - got ${challenge?.options?.auth_code_url}`)
            }

            if (!handle) {
                this.notifySlashID("oauthFlowFailed", {
                    ...authenticationFactor.options,
                    reason: events.OAuthFailureReason.PopupBlocked,
                })
                throw new Error("OIDC flow unsuccesful!")
            }
        } else if (authenticationFactor.options.ux_mode === api.OIDCUXMode.Redirect) {
            window.location.assign(challenge.options.auth_code_url)
        } else {
            throw new Error(`Unsupported OAuth ux_mode - got ${authenticationFactor.options.ux_mode}`)
        }

        this.notifySlashID("oauthFlowStarted", authenticationFactor.options as api.OIDCMethodOptions)

        // no token yet - proxy challenge will result with a token
        return ""
    }

    private async processSAMLChallenge(challenge: api.SAMLChallenge, authenticationFactor: domain.SAMLWithExtras) {
        if (authenticationFactor.options.ux_mode === api.SSOUXMode.Popup) {
            const popup = authenticationFactor.extras.setPopupUrl!!(challenge.options.start_authentication_url)

            if (!challenge?.options?.start_authentication_url) {
                this.notifySlashID("popupFailed", {
                    reason: events.PopupFailureReason.BadAuthURL,
                })
                throw new Error(`Bad authentication URL - got ${challenge?.options?.start_authentication_url}`)
            }

            if (!popup) {
                this.notifySlashID("popupFailed", {
                    reason: events.PopupFailureReason.PopupBlocked,
                })
                throw new Error("SAML flow unsuccesful!")
            }
        } else if (authenticationFactor.options.ux_mode === api.SSOUXMode.Redirect) {
            window.location.assign(challenge.options.start_authentication_url)
        } else {
            throw new Error(`Unsupported SAML ux_mode - got ${authenticationFactor.options.ux_mode}`)
        }

        // no token yet - proxy challenge will result with a token
        return ""
    }

    private async processPasswordSetChallenge(
        challenge: api.PasswordSetChallenge
    ): Promise<api.PasswordSetAttestation> {
        // let the client know the password input is ready
        this.notifySlashID("passwordSetReady")

        // wait for the client to submit the password
        const handlePasswordInput = new Promise<string>((resolve) => {
            const getPassword = (passwordEvent: events.EventsWithNamesMap["passwordSubmitted"]) => {
                if (!this.shouldProcessEvent(passwordEvent)) {
                    return
                }

                // @ts-expect-error payload types do not match because of string & void types
                this.emitter.off("passwordSubmitted", getPassword)
                resolve(passwordEvent.payload)
            }

            // @ts-expect-error payload types do not match because of string & void types
            this.emitter.on("passwordSubmitted", getPassword)
        })

        const password = await handlePasswordInput
        const passwordValidation = validate({ password, rules: createValidationRules(challenge) })

        if (!passwordValidation.valid) {
            this.notifySlashID("invalidPasswordSubmitted", {
                failedRules: passwordValidation.failedRules,
                challengeId: challenge.id,
                authenticationFactor: { method: api.FactorMethod.Password },
                failureDetail: `invalid_password: ${passwordValidation.failedRules.map((rule) => rule.name).join(",")}`,
            })
            return await this.processPasswordSetChallenge(challenge)
        }

        const attestation: api.PasswordSetAttestation = {
            challenge_id: challenge.id,
            challenge_type: challenge.type,
            data: {
                password,
            },
        }

        return attestation
    }

    private processPasswordVerifyChallenge(
        challenge: api.PasswordVerifyChallenge
    ): Promise<api.PasswordVerifyAttestation> {
        // let the client know the password input is ready
        this.notifySlashID("passwordVerifyReady")

        // wait for the client to submit the password
        const handlePasswordInput = new Promise<string>((resolve) => {
            const getPassword = (passwordEvent: events.EventsWithNamesMap["passwordSubmitted"]) => {
                if (!this.shouldProcessEvent(passwordEvent)) {
                    return
                }

                // @ts-expect-error payload types do not match because of string & void types
                this.emitter.off("passwordSubmitted", getPassword)
                resolve(passwordEvent.payload)
            }

            // @ts-expect-error payload types do not match because of string & void types
            this.emitter.on("passwordSubmitted", getPassword)
        })

        return handlePasswordInput.then((password) => {
            if (!password) {
                throw new Error("No password provided")
            }

            const attestation: api.PasswordVerifyAttestation = {
                challenge_id: challenge.id,
                challenge_type: challenge.type,
                data: {
                    password,
                },
            }

            return attestation
        })
    }

    /**
     * TODO complete copy paste of password set challenge
     * TODO write docs
     */
    private async processPasswordResetChallenge(
        challenge: api.PasswordResetChallenge
    ): Promise<api.PasswordResetAttestation> {
        // let the client know the password input is ready
        this.notifySlashID("passwordResetReady")

        // wait for the client to submit the password
        const handlePasswordInput = new Promise<string>((resolve) => {
            const getPassword = (passwordEvent: events.EventsWithNamesMap["passwordSubmitted"]) => {
                if (!this.shouldProcessEvent(passwordEvent)) {
                    return
                }

                // @ts-expect-error payload types do not match because of string & void types
                this.emitter.off("passwordSubmitted", getPassword)
                resolve(passwordEvent.payload)
            }

            // @ts-expect-error payload types do not match because of string & void types
            this.emitter.on("passwordSubmitted", getPassword)
        })

        const password = await handlePasswordInput
        const passwordValidation = validate({ password, rules: createValidationRules(challenge) })

        if (!passwordValidation.valid) {
            this.notifySlashID("invalidPasswordSubmitted", {
                authenticationFactor: { method: api.FactorMethod.Password },
                failedRules: passwordValidation.failedRules,
                challengeId: challenge.id,
                failureDetail: `invalid_password: ${passwordValidation.failedRules.map((rule) => rule.name).join(",")}`,
            })
            return await this.processPasswordSetChallenge(challenge)
        }

        const attestation: api.PasswordResetAttestation = {
            challenge_id: challenge.id,
            challenge_type: challenge.type,
            data: {
                password,
            },
        }

        return attestation
    }

    private async processUiCustomizationChallenge(challenge: api.UICustomizationChallenge): Promise<string> {
        const customizationProperties = challenge.options?.authn_redirect_page_ui_config
        if (!customizationProperties) {
            throw new Error("No customization properties provided!")
        }

        this.notifySlashID("uiCustomizationReceived", {
            customizationProperties,
        })

        // this challenge is processed at the beginning, before actual authentication
        return ""
    }
}
